import WordingMixin from '@/mixins/Wording'
import SizesMixin from '@/mixins/Sizes'
import Experience from './Experience'
import EventHubMixin from '@/mixins/EventHub.js'
import { TweenLite, Power3 } from 'gsap'

export default
{
    name: 'app-experience',

    mixins:
    [
        WordingMixin,
        SizesMixin,
        EventHubMixin
    ],

    data()
    {
        return {
            keypointsActive: false,
            loaderVisible: false,
            circularMenuActive: false,
            messageActive: false
        }
    },

    watch:
    {
        $route(_newRoute)
        {
            this.experience.updateRoute(_newRoute)

            // Keypoints
            this.keypointsActive = _newRoute.meta.experienceFocusScene

            // Loader
            this.loaderVisible = this.experience.loading.step === 1 && this.$route.name !== 'home'
        }
    },

    created()
    {
        this.keypointsActive = this.$route.meta.experienceFocusScene
    },

    mounted()
    {
        this.setExperience()
        this.setLoader()
    },

    destroyed()
    {
        this.experience.destroy()
    },

    methods:
    {
        onResize()
        {
            if(this.keypoints)
            {
                this.keypoints.visibleDistance = this.sizes.width <= 1023 ? 50 : 100
            }

            this.loader.resize()
        },

        onKeypointMouseEnter()
        {
            this.$eventHub.$emit('sound.play', 'keypoint')
        },

        setExperience()
        {
            this.experience = new Experience({
                $eventHub: this.$eventHub,
                grid: this.$root.grid,
                mobile: this.$root.config.platform.mobile,
                $canvas: this.$refs.canvas,
                config: this.$root.config,
                useComposer: !(this.$root.config.platform.browser.name === 'ie' && this.$root.config.platform.browser.version === '11.0'),
                // debug: this.$root.config.name === 'local' || this.$root.config.name === 'preprod'
                debug: false
            })
            this.experience.updateRoute(this.$route)

            // Keypoints
            this.experience.on('keypoints_ready', () =>
            {
                this.setCircularMenu()
                this.setKeypoints()
            })
        },

        setLoader()
        {
            // Loader
            this.loader = {}
            this.loader.$container = this.$refs.loader
            this.loader.$stroke = this.$refs.loaderStroke

            this.loader.resize = () =>
            {
                const grid = this.$root.grid

                const width = grid.amplitude + grid.amplitude - (grid.amplitude - grid.offsetX)
                this.loader.$container.style.width = `${width}px`

                let top = null
                if(this.sizes.width <= 767)
                {
                    top = grid.offsetY + grid.amplitude
                }
                else
                {
                    const lineY = grid.flooredCountY * grid.amplitude + grid.offsetY > this.sizes.height - 180 ? grid.flooredCountY - 1 : grid.flooredCountY
                    top = lineY * grid.amplitude + grid.offsetY
                }

                this.loader.$container.style.top = `${top}px`
            }

            this.experience.loading.loader.on('load', () =>
            {
                const ratio = this.experience.loading.loader.loadedCount / this.experience.loading.loader.toLoadCount

                this.loader.$stroke.style.transform = `scaleX(${ratio})`
            })

            this.experience.loading.loader.on('end', () =>
            {
                // Loader
                this.loaderVisible = this.experience.loading.step === 1 && this.$route.name !== 'home'
            })

            this.loader.resize()
        },

        setKeypoints()
        {
            // Set up
            this.keypoints = {}
            this.keypoints.$container = this.$refs.keypointContainer
            this.keypoints.$elements = this.$refs.keypoints
            this.keypoints.visibleDistance = this.sizes.width <= 1023 ? 50 : 100
            this.keypoints.radius = 25
            this.keypoints.items = []

            const targets = {
                nanopix: this.experience.world.scenes.protect.nanopix.roverA.container,
                rover: this.experience.world.scenes.protect.nanopix.roverA.container,
                tablet1: this.experience.world.scenes.validate.tablet.container,
                tablet2: this.experience.world.scenes.validate.tablet.container,
                tablet3: this.experience.world.scenes.validate.tablet.container,
                simulator1: this.experience.world.scenes.training.simulator.container,
                simulator2: this.experience.world.scenes.training.simulator.container,
                simulator3: this.experience.world.scenes.training.simulator.container,
                drone1: this.experience.world.scenes.investigate.drone.container,
                drone2: this.experience.world.scenes.investigate.drone.container
            }

            for(const _keypoint of this.wording.experience.keypoints)
            {
                const $element = this.keypoints.$elements.find((_item) => _item.classList.contains(`keypoint-${_keypoint.class}`))
                const target = targets[_keypoint.class]
                const keypoint = this.experience.keypoints.add({ $element: $element, ..._keypoint.position, target: target })

                $element.addEventListener('mouseenter', () =>
                {
                    this.keypoints.$container.classList.add('hover')
                    $element.classList.add('hover')
                })

                $element.addEventListener('mouseleave', () =>
                {
                    this.keypoints.$container.classList.remove('hover')
                    $element.classList.remove('hover')
                })

                // $element.addEventListener('touchstart', () =>
                // {
                //     this.keypoints.$container.classList.add('hover')
                //     $element.classList.add('hover')

                //     window.addEventListener('touchstart', () =>
                //     {

                //     })
                // })

                keypoint.scene = _keypoint.scene

                this.keypoints.items.push(keypoint)
            }

            for(const item of this.keypoints.items)
            {
                item.visible = false
                item.inScreen = false
            }

            // Use experience tick to prevent frame offset
            this.experience.time.on('tick', () =>
            {
                if(this.keypointsActive)
                {
                    for(const item of this.keypoints.items)
                    {
                        const inScreen = item.x > - this.keypoints.radius && item.x < this.sizes.width + this.keypoints.radius && item.y > - this.keypoints.radius && item.y < this.sizes.height + this.keypoints.radius
                        const isCurrentScene = item.scene === this.$route.meta.experienceKeypointScene
                        const visible = isCurrentScene && item.x > this.keypoints.visibleDistance && item.x < this.sizes.width - this.keypoints.visibleDistance && item.y > this.keypoints.visibleDistance && item.y < this.sizes.height - this.keypoints.visibleDistance

                        if(visible !== item.visible)
                        {
                            item.visible = visible

                            if(item.visible)
                            {
                                item.$element.classList.add('is-visible')
                            }
                            else
                            {
                                item.$element.classList.remove('is-visible')
                            }
                        }

                        if(item.inScreen)
                        {
                            item.$element.style.transform = `translateX(${Math.round(item.x * 10) / 10}px) translateY(${Math.round(item.y * 10) / 10}px)`
                        }

                        item.inScreen = inScreen
                    }
                }
            })
        },

        setCircularMenu()
        {
            this.circularMenu = {}
            this.circularMenu.hasTouch = false
            this.circularMenu.enable = true
            this.circularMenu.entered = false

            /**
             * Should render
             */
            this.circularMenu.shouldRender = () =>
            {
                if(this.circularMenu.cursor.radius.current > 0)
                {
                    return true
                }

                for(const _section of this.circularMenu.sections.items)
                {
                    if(_section.idleProgress > 0)
                    {
                        return true
                    }
                }

                return false
            }

            /**
             * Active
             */
            this.circularMenu.active = false

            this.circularMenu.activate = () =>
            {
                if(this.circularMenu.enable && this.circularMenu.sections.items.length > 0 && !this.$root.config.platform.mobile)
                {
                    this.circularMenuActive = true

                    // For touch
                    if(this.circularMenu.hasTouch)
                    {
                        this.circularMenu.enter()

                        this.circularMenu.center.x = this.circularMenu.container.width * 0.5
                        this.circularMenu.center.y = this.circularMenu.container.height * 0.5
                        this.circularMenu.center.target.x = this.circularMenu.center.x
                        this.circularMenu.center.target.y = this.circularMenu.center.y
                        this.circularMenu.center.rounded.x = this.circularMenu.center.x
                        this.circularMenu.center.rounded.y = this.circularMenu.center.y
                    }
                }
            }

            this.circularMenu.deactivate = () =>
            {
                this.circularMenuActive = false
                this.messageActive = false

                // For touch
                if(this.circularMenu.hasTouch)
                {
                    this.circularMenu.leave()
                }
            }

            this.$eventHub.$on('experience.playFocusStart', this.circularMenu.deactivate)
            this.$eventHub.$on('experience.quitFocusEnd', this.circularMenu.activate)
            this.$eventHub.$on('experience.introEnd', this.circularMenu.activate)
            this.$eventHub.$on('experience.camera.transitionEnd', this.circularMenu.activate)
            this.$eventHub.$on('experience.camera.transitionStart', this.circularMenu.deactivate)
            this.$eventHub.$on('experience.camera.transitionStart', () =>
            {
                console.log('experience.camera.transitionStart')
            })

            /**
             * Enter / Leave
             */
            this.circularMenu.enter = () =>
            {
                // Already entered
                if(this.circularMenu.entered)
                {
                    return
                }

                // Save
                this.circularMenu.entered = true

                // Cursor
                TweenLite.killTweensOf(this.circularMenu.cursor.color.current)
                TweenLite.killTweensOf(this.circularMenu.cursor.radius)
                TweenLite.killTweensOf(this.circularMenu.cursor.lineWidth)
                TweenLite.to(this.circularMenu.cursor.color.current, 0.5, { r: this.circularMenu.cursor.color.base.r, g: this.circularMenu.cursor.color.base.g, b: this.circularMenu.cursor.color.base.b })
                TweenLite.to(this.circularMenu.cursor.radius, 0.5, { current: this.circularMenu.cursor.radius.base })
                TweenLite.to(this.circularMenu.cursor.lineWidth, 0.5, { current: this.circularMenu.cursor.lineWidth.base })

                // Sections
                TweenLite.killTweensOf(this.circularMenu.sections)
                TweenLite.to(this.circularMenu.sections, 0.3, { angle: - this.circularMenu.sections.amplitude * 0.5 - (this.circularMenu.sections.amplitude * this.circularMenu.sections.closeProgress) * 0.5 })

                for(const _section of this.circularMenu.sections.items)
                {
                    TweenLite.killTweensOf(_section)
                    TweenLite.to(_section, 0.5, { idleProgress: this.circularMenu.sections.closeProgress, activeProgress: 0 })
                }
            }

            this.circularMenu.leave = () =>
            {
                // Already leaved
                if(!this.circularMenu.entered)
                {
                    return
                }

                // Cannot leave because opened
                if(this.circularMenu.opened)
                {
                    return
                }

                // Save
                this.circularMenu.entered = false

                // Cursor
                TweenLite.killTweensOf(this.circularMenu.cursor.color.current)
                TweenLite.killTweensOf(this.circularMenu.cursor.radius)
                TweenLite.killTweensOf(this.circularMenu.cursor.lineWidth)
                TweenLite.to(this.circularMenu.cursor.color.current, 0.5, { r: this.circularMenu.cursor.color.hidden.r, g: this.circularMenu.cursor.color.hidden.g, b: this.circularMenu.cursor.color.hidden.b })
                TweenLite.to(this.circularMenu.cursor.radius, 0.5, { current: this.circularMenu.cursor.radius.hidden })
                TweenLite.to(this.circularMenu.cursor.lineWidth, 0.5, { current: this.circularMenu.cursor.lineWidth.hidden })

                // Sections
                TweenLite.killTweensOf(this.circularMenu.sections)
                TweenLite.to(this.circularMenu.sections, 0.5, { angle: - Math.PI })

                for(const _section of this.circularMenu.sections.items)
                {
                    TweenLite.killTweensOf(_section)
                    TweenLite.to(_section, 0.5, { idleProgress: 0 })
                }
            }

            /**
             * Open / Close
             */
            this.circularMenu.opened = false

            this.circularMenu.open = () =>
            {
                // Already opened
                if(this.circularMenu.opened)
                {
                    return
                }

                // Save
                this.circularMenu.opened = true

                // Close message
                this.messageActive = false

                // Event
                this.$eventHub.$emit('circularMenu.open')

                // Camera
                this.experience.camera.angles.activate()

                // Cursor
                TweenLite.killTweensOf(this.circularMenu.cursor.color.current)
                TweenLite.killTweensOf(this.circularMenu.cursor.radius)
                TweenLite.killTweensOf(this.circularMenu.cursor.lineWidth)
                TweenLite.to(this.circularMenu.cursor.color.current, 0.3, { r: this.circularMenu.cursor.color.destination.r, g: this.circularMenu.cursor.color.destination.g, b: this.circularMenu.cursor.color.destination.b })
                TweenLite.to(this.circularMenu.cursor.radius, 0.3, { current: this.circularMenu.cursor.radius.destination })
                TweenLite.to(this.circularMenu.cursor.lineWidth, 0.3, { current: this.circularMenu.cursor.lineWidth.destination })

                // Sections
                TweenLite.killTweensOf(this.circularMenu.sections)
                TweenLite.to(this.circularMenu.sections, 0.3, { angle: - this.circularMenu.sections.amplitude * 0.5 })

                let i = 0
                for(const _section of this.circularMenu.sections.items)
                {
                    TweenLite.killTweensOf(_section)
                    TweenLite.killTweensOf(_section.radius)
                    TweenLite.to(_section, 0.6, { idleProgress: 1, delay: 0.035 * i, ease: Power3.easeInOut })
                    TweenLite.to(_section.radius, 0.6, { current: _section.radius.destination, delay: 0.035 * i, ease: Power3.easeInOut })

                    i++
                }

                if(this.circularMenu.sections.index !== - 1)
                {
                    const currentSection = this.circularMenu.sections.items[this.circularMenu.sections.index]
                    TweenLite.to(currentSection, 0.4, { activeProgress: 1, ease: Power3.easeInOut })
                }
            }

            this.circularMenu.close = () =>
            {
                // Already closed
                if(!this.circularMenu.opened)
                {
                    return
                }

                // Save
                this.circularMenu.opened = false

                // Event
                this.$eventHub.$emit('circularMenu.close')

                // Cursor
                TweenLite.killTweensOf(this.circularMenu.cursor)
                TweenLite.killTweensOf(this.circularMenu.cursor.color.current)
                TweenLite.killTweensOf(this.circularMenu.cursor.radius)
                TweenLite.killTweensOf(this.circularMenu.cursor.lineWidth)
                TweenLite.to(this.circularMenu.cursor, 0.3, { x: 0, y: 0 })
                TweenLite.to(this.circularMenu.cursor.color.current, 0.3, { r: this.circularMenu.cursor.color.base.r, g: this.circularMenu.cursor.color.base.g, b: this.circularMenu.cursor.color.base.b })
                TweenLite.to(this.circularMenu.cursor.radius, 0.3, { current: this.circularMenu.cursor.radius.base })
                TweenLite.to(this.circularMenu.cursor.lineWidth, 0.3, { current: this.circularMenu.cursor.lineWidth.base })

                // Sections
                TweenLite.killTweensOf(this.circularMenu.sections)
                TweenLite.to(this.circularMenu.sections, 0.3, { angle: - this.circularMenu.sections.amplitude * 0.5 - (this.circularMenu.sections.amplitude * this.circularMenu.sections.closeProgress) * 0.5 })

                for(const _section of this.circularMenu.sections.items)
                {
                    TweenLite.killTweensOf(_section)
                    TweenLite.killTweensOf(_section.radius)
                    TweenLite.to(_section, 0.6, { idleProgress: this.circularMenu.sections.closeProgress, activeProgress: 0, ease: Power3.easeInOut })
                    TweenLite.to(_section.radius, 0.6, { current: _section.radius.base, ease: Power3.easeInOut })
                }

                // Camera
                this.experience.camera.angles.deactivate()
            }

            /**
             * Move
             */
            this.circularMenu.move = (_x, _y) =>
            {
                const relativeToContainerX = _x - this.circularMenu.container.x
                const relativeToContainerY = _y - this.circularMenu.container.y

                const relativeToCenterX = relativeToContainerX - this.circularMenu.center.target.x
                const relativeToCenterY = relativeToContainerY - this.circularMenu.center.target.y

                if(this.circularMenu.opened)
                {
                    const angle = Math.atan2(relativeToCenterY, relativeToCenterX)
                    const progress = (angle / (Math.PI * 2) + 3) % 1
                    const distance = Math.sqrt(Math.pow(relativeToCenterX, 2) + Math.pow(relativeToCenterY, 2))

                    if(this.experience.camera.type === 'guided' && distance > 20)
                    {
                        this.experience.camera.angles.progress = progress
                    }

                    this.circularMenu.cursor.x = Math.log(Math.abs(relativeToCenterX / 50) + 1) * 50 * (relativeToCenterX < 0 ? - 1 : 1)
                    this.circularMenu.cursor.y = Math.log(Math.abs(relativeToCenterY / 50) + 1) * 50 * (relativeToCenterY < 0 ? - 1 : 1)
                }
                else
                {
                    this.circularMenu.center.target.x = relativeToContainerX
                    this.circularMenu.center.target.y = relativeToContainerY
                }
            }

            /**
             * Container
             */
            this.circularMenu.container = {}
            this.circularMenu.container.$element = this.$refs.circularMenu
            this.circularMenu.container.x = 0
            this.circularMenu.container.y = 0
            this.circularMenu.container.width = 400
            this.circularMenu.container.height = 800

            // Mouse events
            this.circularMenu.containerMouseEnter = () =>
            {
                this.circularMenu.hasTouch = false

                this.circularMenu.enter()
            }
            this.circularMenu.containerMouseLeave = () =>
            {
                this.circularMenu.hasTouch = false

                this.circularMenu.leave()
            }
            this.circularMenu.containerMouseMove = (_event) =>
            {
                this.circularMenu.hasTouch = false

                this.circularMenu.move(_event.clientX, _event.clientY)
            }
            this.circularMenu.containerMouseDown = () =>
            {
                this.circularMenu.hasTouch = false

                this.circularMenu.open()

                window.addEventListener('mouseup', this.circularMenu.containerMouseUp)

                // Switch mousemove to window
                this.circularMenu.container.$element.removeEventListener('mousemove', this.circularMenu.containerMouseMove)
                window.addEventListener('mousemove', this.circularMenu.containerMouseMove)
            }
            this.circularMenu.containerMouseUp = () =>
            {
                this.circularMenu.hasTouch = false

                this.circularMenu.close()

                window.removeEventListener('mouseup', this.circularMenu.containerMouseUp)

                // Switch mousemove to container
                window.removeEventListener('mousemove', this.circularMenu.containerMouseMove)
                this.circularMenu.container.$element.addEventListener('mousemove', this.circularMenu.containerMouseMove)
            }

            this.circularMenu.container.$element.addEventListener('mouseenter', this.circularMenu.containerMouseEnter)
            this.circularMenu.container.$element.addEventListener('mouseleave', this.circularMenu.containerMouseLeave)
            this.circularMenu.container.$element.addEventListener('mousemove', this.circularMenu.containerMouseMove)
            this.circularMenu.container.$element.addEventListener('mousedown', this.circularMenu.containerMouseDown)

            // Touch events
            this.circularMenu.containerTouchMove = (_event) =>
            {
                this.circularMenu.hasTouch = true

                this.circularMenu.move(_event.changedTouches[0].screenX, _event.changedTouches[0].screenY)
            }
            this.circularMenu.containerTouchStart = (_event) =>
            {
                this.circularMenu.hasTouch = true

                this.circularMenu.move(_event.changedTouches[0].screenX, _event.changedTouches[0].screenY)
                // this.circularMenu.enter()
                this.circularMenu.open()

                window.addEventListener('touchend', this.circularMenu.containerTouchEnd)
                window.addEventListener('touchmove', this.circularMenu.containerTouchMove)
            }
            this.circularMenu.containerTouchEnd = () =>
            {
                this.circularMenu.hasTouch = true

                this.circularMenu.close()
                // this.circularMenu.leave()

                window.removeEventListener('touchend', this.circularMenu.containerTouchEnd)
                window.removeEventListener('touchmove', this.circularMenu.containerTouchMove)
            }

            this.circularMenu.container.$element.addEventListener('touchstart', this.circularMenu.containerTouchStart)

            /**
             * Center
             */
            this.circularMenu.center = {}
            this.circularMenu.center.x = 0
            this.circularMenu.center.y = 0
            this.circularMenu.center.target = {}
            this.circularMenu.center.target.x = 0
            this.circularMenu.center.target.y = 0
            this.circularMenu.center.rounded = {}
            this.circularMenu.center.rounded.x = 0
            this.circularMenu.center.rounded.y = 0
            this.circularMenu.center.easing = 0.004
            this.circularMenu.$center = this.$refs.circularMenuCenter

            /**
             * Animation
             */
            this.circularMenu.animation = {}
            this.circularMenu.animation.$element = this.$el.querySelector('.js-animation')
            this.circularMenu.animation.context = this.circularMenu.animation.$element.getContext('2d')
            this.circularMenu.animation.size = 320
            this.circularMenu.animation.half = this.circularMenu.animation.size * 0.5

            /**
             * Cursor
             */
            this.circularMenu.cursor = {}
            this.circularMenu.cursor.color = {}
            this.circularMenu.cursor.color.hidden = { r: 255, g: 255, b: 255 }
            this.circularMenu.cursor.color.base = { r: 255, g: 255, b: 255 }
            this.circularMenu.cursor.color.destination = { r: 255, g: 230, b: 0 }
            this.circularMenu.cursor.color.current = { r: 255, g: 255, b: 255 }
            this.circularMenu.cursor.radius = {}
            this.circularMenu.cursor.radius.hidden = 0
            this.circularMenu.cursor.radius.base = 10
            this.circularMenu.cursor.radius.destination = 5
            this.circularMenu.cursor.radius.current = 0
            this.circularMenu.cursor.lineWidth = {}
            this.circularMenu.cursor.lineWidth.hidden = 0
            this.circularMenu.cursor.lineWidth.base = 2
            this.circularMenu.cursor.lineWidth.destination = 10
            this.circularMenu.cursor.lineWidth.current = 0
            this.circularMenu.cursor.x = 0
            this.circularMenu.cursor.y = 0

            /**
             * Sections
             */
            this.circularMenu.sections = {}
            this.circularMenu.sections.closeProgress = 0.1
            this.circularMenu.sections.separation = Math.PI * 2 * 0.01
            this.circularMenu.sections.items = []
            this.circularMenu.sections.angle = - Math.PI * 0.5
            this.circularMenu.sections.index = - 1

            if(this.experience.camera.type === 'guided')
            {
                this.experience.camera.on('anglesChanged', (_angles) =>
                {
                    this.circularMenu.sections.items = []
                    this.circularMenu.sections.index = - 1

                    // Has angles
                    if(_angles)
                    {
                        this.circularMenu.sections.amplitude = Math.PI * 2 / _angles.length

                        for(let i = 0; i < _angles.length; i++)
                        {
                            const section = {}

                            section.lineWidth = 2

                            section.radius = {}
                            section.radius.base = 20
                            section.radius.current = section.radius.base
                            section.radius.destination = Math.round(this.circularMenu.animation.half - section.lineWidth * 0.5)

                            section.idleProgress = 0
                            section.activeProgress = 0

                            this.circularMenu.sections.items.push(section)
                        }
                    }
                })

                this.experience.camera.on('anglesIndexChanged', (_index) =>
                {
                    if(this.circularMenu.sections.index !== -1)
                    {
                        const previousSection = this.circularMenu.sections.items[this.circularMenu.sections.index]
                        TweenLite.to(previousSection, 0.4, { activeProgress: 0, ease: Power3.easeInOut })
                    }

                    const newSection = this.circularMenu.sections.items[_index]
                    TweenLite.to(newSection, 1, { activeProgress: 1, ease: Power3.easeInOut })

                    this.circularMenu.sections.index = _index
                })
            }

            /**
             * Resize
             */
            let firstResize = true
            this.circularMenu.resize = () =>
            {
                // Container
                const containerBounding = this.circularMenu.container.$element.getBoundingClientRect()
                this.circularMenu.container.x = containerBounding.left
                this.circularMenu.container.y = containerBounding.top
                this.circularMenu.container.width = containerBounding.width
                this.circularMenu.container.height = containerBounding.height

                // Center
                if(firstResize)
                {
                    firstResize = false

                    this.circularMenu.center.x = containerBounding.width * 0.5
                    this.circularMenu.center.y = containerBounding.height * 0.5
                    this.circularMenu.center.target.x = this.circularMenu.center.x
                    this.circularMenu.center.target.y = this.circularMenu.center.y
                    this.circularMenu.center.rounded.x = this.circularMenu.center.target.x
                    this.circularMenu.center.rounded.y = this.circularMenu.center.target.y
                }
            }
            this.circularMenu.resize()

            /**
             * Tick
             */
            this.experience.time.on('tick', () =>
            {
                // Render if visible
                if(!this.circularMenu.shouldRender())
                {
                    return
                }

                // Center
                const targetX = this.circularMenu.center.target.x + this.circularMenu.cursor.x * 0.35
                const targetY = this.circularMenu.center.target.y + this.circularMenu.cursor.y * 0.35

                const x = this.circularMenu.center.x + (targetX - this.circularMenu.center.x) * this.experience.time.delta * this.circularMenu.center.easing
                const y = this.circularMenu.center.y + (targetY - this.circularMenu.center.y) * this.experience.time.delta * this.circularMenu.center.easing

                const deltaX = Math.abs(x - this.circularMenu.center.x)
                const deltaY = Math.abs(y - this.circularMenu.center.y)

                const speed = Math.hypot(deltaX, deltaY)

                this.messageActive = this.circularMenuActive && !this.circularMenu.opened && speed < 0.02

                this.circularMenu.center.x = x
                this.circularMenu.center.y = y

                const roundedX = Math.round(this.circularMenu.center.x * 10) / 10
                const roundedY = Math.round(this.circularMenu.center.y * 10) / 10

                if(roundedX !== this.circularMenu.center.rounded.x || roundedY !== this.circularMenu.center.rounded.y)
                {
                    this.circularMenu.center.rounded.x = roundedX
                    this.circularMenu.center.rounded.y = roundedY

                    this.circularMenu.$center.style.transform = `translate(${this.circularMenu.center.rounded.x}px, ${this.circularMenu.center.rounded.y}px)`
                }

                // Animation clear
                this.circularMenu.animation.context.clearRect(0, 0, this.circularMenu.animation.size, this.circularMenu.animation.size)

                // Animation cursor
                this.circularMenu.animation.context.strokeStyle = `rgb(${this.circularMenu.cursor.color.current.r}, ${this.circularMenu.cursor.color.current.g}, ${this.circularMenu.cursor.color.current.b})`
                this.circularMenu.animation.context.lineWidth = this.circularMenu.cursor.lineWidth.current
                this.circularMenu.animation.context.beginPath()
                this.circularMenu.animation.context.arc(this.circularMenu.animation.half + this.circularMenu.cursor.x, this.circularMenu.animation.half + this.circularMenu.cursor.y, this.circularMenu.cursor.radius.current, 0, Math.PI * 2)
                this.circularMenu.animation.context.stroke()

                // Animation sections
                let i = 0
                for(const _section of this.circularMenu.sections.items)
                {
                    // Idle
                    const idleAngleStart = i * this.circularMenu.sections.amplitude + this.circularMenu.sections.angle
                    const idleAngleLength = (this.circularMenu.sections.amplitude - this.circularMenu.sections.separation) * _section.idleProgress
                    const idleOpacity = 0.35 + (1 - _section.idleProgress) * 0.65

                    this.circularMenu.animation.context.strokeStyle = `rgba(255, 255, 255, ${idleOpacity})`
                    this.circularMenu.animation.context.lineWidth = _section.lineWidth
                    this.circularMenu.animation.context.beginPath()
                    this.circularMenu.animation.context.arc(this.circularMenu.animation.half, this.circularMenu.animation.half, _section.radius.current, idleAngleStart, idleAngleStart + idleAngleLength)
                    this.circularMenu.animation.context.stroke()

                    // Active
                    const activeAngleStart = i * this.circularMenu.sections.amplitude + this.circularMenu.sections.angle + (1 - _section.activeProgress) * this.circularMenu.sections.amplitude * 0.5
                    const activeAngleLength = (this.circularMenu.sections.amplitude - this.circularMenu.sections.separation) * _section.activeProgress

                    this.circularMenu.animation.context.strokeStyle = 'rgba(255, 255, 255, 1)'
                    this.circularMenu.animation.context.lineWidth = _section.lineWidth
                    this.circularMenu.animation.context.beginPath()
                    this.circularMenu.animation.context.arc(this.circularMenu.animation.half, this.circularMenu.animation.half, _section.radius.current, activeAngleStart, activeAngleStart + activeAngleLength)
                    this.circularMenu.animation.context.stroke()

                    i++
                }
            })
        }
    }
}



// WEBPACK FOOTER //
// ./src/components/app-experience/script.js